/**
 * Device preview (spec 013): the editor's connection to phones playing the
 * scene being edited.
 *
 * One `DevicePreviewController` per editor tab holds the session and at most
 * one controller socket. It is a small state machine:
 *
 *   idle → creating → waiting → connected(n) → idle
 *   error(message) from creating and waiting
 *
 * `createSession` and `connect` are idempotent: a call while one is in flight
 * or open returns the same session and socket, so React StrictMode's double
 * mount makes one session and one socket. The session survives closing the
 * modal and switching scenes; it ends on Disconnect or when the tab closes.
 * An unexpected close reconnects with backoff (1, 2, 4, 8 s, then every
 * 10 s); a 4001 close (the session was opened in another tab) goes to idle.
 *
 * `useDeviceSceneSync` (below) sends the scene editor's content: on a player
 * joining, on Preview, and on edits (debounced 300 ms), never on playhead
 * moves; scrubbing while paused sends `seek` at most 10 times a second.
 *
 * The controller also holds what the device plays, `content` (spec 013 v7):
 * the scene (default) or a story run `{ storyId, runId, startAtStorySceneId?,
 * label }`, started by the story editor's Device menu (`startStoryRun`).
 * `useDeviceStorySync` sends the story's `story-data` while the content is a
 * story; the scene sync stays quiet meanwhile.
 */

import React, { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { apiUrl } from '../Config';
import { notifyUnauthorized, storedToken } from '../api/session';
import { useSceneStore } from '../stores/sceneStore';
import { useStoryStore } from '../stores/storyStore';
import { buildPreviewManifest } from '../utils/previewRunner';
import { useMediaSheetStore } from '../stores/mediaSheetStore';
import { loadStoryPreviewScenes } from '../utils/previewSources';
import type { PlayableScene, StoryApiResponse, StoryPackageManifest } from '../../../Shared/src/@types';

export type DeviceStatus = 'idle' | 'creating' | 'waiting' | 'connected' | 'error';

/** What the device plays: the scene being edited, or a run of a story. */
export type DeviceContent =
  | { kind: 'scene' }
  | {
    kind: 'story';
    storyId: number;
    /** One more than the last run of this controller; the same id on an edit. */
    runId: number;
    /** Absent = the whole story, from its start. */
    startAtStorySceneId?: number;
    /** The start scene's name ("From “…”"); absent for the whole story. */
    label?: string;
  };

/** `story-data`'s payload. `startAtStorySceneId` is absent, never `null` (JSON drops nulls). */
export interface StoryDevicePayload {
  storyId: number;
  runId: number;
  startAtStorySceneId?: number;
  manifest: StoryPackageManifest;
}

const SCENE_CONTENT: DeviceContent = { kind: 'scene' };

export interface DevicePreviewState {
  status: DeviceStatus;
  /** Players on the session (connected ⇔ the socket is open and this is > 0). */
  playerCount: number;
  /** The error (status `error`) or why the session ended (status `idle`). */
  message: string | null;
  sessionId: string | null;
  qrCode: string | null;
  playerUrl: string | null;
  /** "Remote": Preview plays on the device. */
  previewOnDevice: boolean;
  /** What the device plays (spec 013 v7). */
  content: DeviceContent;
}

export interface DeviceSession {
  sessionId: string;
  playerUrl: string;
  qrCode: string;
  wsUrl: string;
}

/** Close codes from the Service (routes/devicePreview.ts). */
export const CLOSE_REPLACED = 4001;
export const CLOSE_UNAUTHORISED = 4003;
export const CLOSE_UNKNOWN_SESSION = 4004;

export const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000];
export const RECONNECT_STEADY_MS = 10_000;
export const HEARTBEAT_MS = 10_000;
export const OPENED_ELSEWHERE = 'Opened in another tab';

const SOCKET_OPEN = 1;

const INITIAL_STATE: DevicePreviewState = {
  status: 'idle',
  playerCount: 0,
  message: null,
  sessionId: null,
  qrCode: null,
  playerUrl: null,
  previewOnDevice: false,
  content: SCENE_CONTENT,
};

/**
 * A new story run id: greater than the last, and unique across controllers
 * (milliseconds since the epoch × 1000 plus a random 0–999, a safe integer),
 * so a phone that keeps a run across controller loss (D-7) never takes a
 * fresh controller's first run for an edit of its own.
 */
export function nextRunId(last: number, now: number = Date.now(), random: number = Math.random()): number {
  return Math.max(last + 1, now * 1000 + Math.floor(random * 1000));
}

export class DevicePreviewController {
  private state: DevicePreviewState = INITIAL_STATE;
  private readonly listeners = new Set<() => void>();
  private readonly joinListeners = new Set<() => void>();
  private ws: WebSocket | null = null;
  private wsBase: string | null = null;
  private creating: Promise<DeviceSession | null> | null = null;
  private session: DeviceSession | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private attempt = 0;
  private disposeTimer: ReturnType<typeof setTimeout> | null = null;
  /** The last run started. Run ids are seeded from the clock, so a new controller (a reloaded page,
   *  another tab) never reuses the id of a run a phone still holds (D-7). */
  private lastRunId = 0;

  // ── Store ────────────────────────────────────────────────────────────────

  getState = (): DevicePreviewState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<DevicePreviewState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  /** Called when a player joins the session (to send it the scene). */
  onPlayerJoined = (listener: () => void): (() => void) => {
    this.joinListeners.add(listener);
    return () => this.joinListeners.delete(listener);
  };

  // ── Session ──────────────────────────────────────────────────────────────

  /** Create the session (or return the one in flight or open) and connect its socket. */
  createSession = (): Promise<DeviceSession | null> => {
    if (this.creating) return this.creating;
    if (this.session && this.state.status !== 'error') {
      this.connect();
      return Promise.resolve(this.session);
    }
    this.set({ status: 'creating', message: null });
    this.creating = this.requestSession(this.session?.sessionId)
      .then((session) => {
        this.adopt(session);
        this.connect();
        return session;
      })
      .catch((err: unknown) => {
        this.set({ status: 'error', message: err instanceof Error ? err.message : 'Could not create a session' });
        return null;
      })
      .finally(() => {
        this.creating = null;
      });
    return this.creating;
  };

  private adopt(session: DeviceSession): void {
    this.session = session;
    this.wsBase = session.wsUrl.replace(/\/+$/, '');
    this.set({
      sessionId: session.sessionId,
      qrCode: session.qrCode,
      playerUrl: session.playerUrl,
      status: this.state.playerCount > 0 && this.ws ? 'connected' : 'waiting',
      message: null,
    });
  }

  private async requestSession(sessionId?: string): Promise<DeviceSession> {
    const token = storedToken();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(`${apiUrl}/device-preview/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'X-Auth-Token': token } : {}) },
        body: JSON.stringify(sessionId ? { sessionId } : {}),
        signal: controller.signal,
      });
      if (res.status === 401) notifyUnauthorized(token);
      if (!res.ok) throw new Error(`Could not create a session (HTTP ${res.status})`);
      const data = (await res.json()) as DeviceSession;
      if (!data?.sessionId || !data.wsUrl) throw new Error('Could not create a session (bad response)');
      return data;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new Error('Connection timed out — is the service running?');
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }

  // ── Socket ───────────────────────────────────────────────────────────────

  /** Open the controller socket for the session; a no-op while one is open or opening. */
  connect = (): void => {
    if (this.ws || !this.session || !this.wsBase) return;
    this.clearReconnect();
    const token = storedToken() ?? '';
    const url = `${this.wsBase}/?session=${encodeURIComponent(this.session.sessionId)}&role=controller&token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.set({ status: this.state.playerCount > 0 ? 'connected' : 'waiting', message: null });
      this.heartbeatTimer = setInterval(() => this.send({ type: 'heartbeat' }), HEARTBEAT_MS);
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.ws !== ws) return;
      let message: { type?: string; playerCount?: number };
      try {
        message = JSON.parse(String(event.data));
      } catch {
        return;
      }
      switch (message.type) {
        case 'player-connected':
        case 'player-ready':
        case 'player-disconnected': {
          const n = Math.max(0, Number(message.playerCount) || 0);
          const joined = message.type === 'player-connected';
          this.set({
            playerCount: n,
            status: n > 0 ? 'connected' : 'waiting',
            previewOnDevice: joined ? true : n > 0 && this.state.previewOnDevice,
          });
          if (joined) this.joinListeners.forEach((l) => l());
          break;
        }
        case 'session-state': {
          // Sent on every (re)connect: players already in the session get
          // the scene again, since edits made while offline never reached them.
          const n = Math.max(0, Number(message.playerCount) || 0);
          this.set({
            playerCount: n,
            status: n > 0 ? 'connected' : 'waiting',
            previewOnDevice: n > 0 && this.state.previewOnDevice,
          });
          if (n > 0) this.joinListeners.forEach((l) => l());
          break;
        }
        default:
          break;
      }
    };

    ws.onclose = (event: CloseEvent) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearHeartbeat();
      if (event.code === CLOSE_REPLACED) {
        this.reset({ message: OPENED_ELSEWHERE });
        return;
      }
      if (event.code === CLOSE_UNAUTHORISED) {
        this.set({ status: 'error', message: 'This session belongs to another account', playerCount: 0 });
        return;
      }
      this.set({ status: 'waiting', playerCount: 0 });
      this.scheduleReconnect(event.code === CLOSE_UNKNOWN_SESSION);
    };

    ws.onerror = () => {
      // onclose follows and reconnects.
    };
  };

  private scheduleReconnect(refreshSession: boolean): void {
    if (this.reconnectTimer || !this.session) return;
    const delay = RECONNECT_DELAYS_MS[this.attempt] ?? RECONNECT_STEADY_MS;
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.session) return;
      if (!refreshSession) {
        this.connect();
        return;
      }
      // The Service no longer knows the session (it restarted): ask it again by id.
      this.requestSession(this.session.sessionId)
        .then((session) => {
          if (!this.session) return;
          this.adopt(session);
          this.connect();
        })
        .catch(() => this.scheduleReconnect(true));
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private reset(patch: Partial<DevicePreviewState> = {}): void {
    this.clearReconnect();
    this.clearHeartbeat();
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.session = null;
    this.wsBase = null;
    this.attempt = 0;
    this.set({ ...INITIAL_STATE, content: this.state.content, ...patch });
  }

  /** End the session (Disconnect). */
  disconnect = (): void => this.reset();

  // ── Messages ─────────────────────────────────────────────────────────────

  private send(message: object): void {
    if (this.ws && this.ws.readyState === SOCKET_OPEN) this.ws.send(JSON.stringify(message));
  }

  sendSceneData = (scene: PlayableScene): void => this.send({ type: 'scene-data', data: { scene } });
  sendStoryData = (data: StoryDevicePayload): void => this.send({ type: 'story-data', data });

  /**
   * Play a story on the device (the story editor's Device menu): a new run,
   * from `startAtStorySceneId` or (absent) the whole story. `useDeviceStorySync`
   * sends it, at once when a device is connected, else when one joins.
   */
  startStoryRun = (run: { storyId: number; startAtStorySceneId?: number; label?: string }): DeviceContent => {
    this.lastRunId = nextRunId(this.lastRunId);
    const content: DeviceContent = {
      kind: 'story',
      storyId: run.storyId,
      runId: this.lastRunId,
      ...(run.startAtStorySceneId !== undefined ? { startAtStorySceneId: run.startAtStorySceneId } : {}),
      ...(run.label !== undefined ? { label: run.label } : {}),
    };
    this.set({ content });
    return content;
  };

  /** The device plays the scene again (nothing is sent; the scene sync sends by its rules). */
  setSceneContent = (): void => {
    if (this.state.content.kind !== 'scene') this.set({ content: SCENE_CONTENT });
  };

  startDevicePreview = (playhead = 0): void => this.send({ type: 'preview-start', playhead });
  stopDevicePreview = (): void => this.send({ type: 'preview-stop' });
  seekDevice = (playhead: number): void => this.send({ type: 'seek', playhead });

  setPreviewOnDevice = (value: boolean): void => this.set({ previewOnDevice: value });

  // ── Lifetime (StrictMode unmounts and remounts the provider) ──────────────

  attach(): void {
    if (this.disposeTimer) clearTimeout(this.disposeTimer);
    this.disposeTimer = null;
  }

  detach(): void {
    this.disposeTimer = setTimeout(() => this.reset(), 0);
  }
}

export interface DevicePreviewContextType extends DevicePreviewState {
  /** The socket is open and at least one device plays. */
  isConnected: boolean;
  controller: DevicePreviewController;
  createSession: () => Promise<DeviceSession | null>;
  connect: () => void;
  disconnect: () => void;
  sendSceneData: (scene: PlayableScene) => void;
  startDevicePreview: (playhead?: number) => void;
  stopDevicePreview: () => void;
  seekDevice: (playhead: number) => void;
  setPreviewOnDevice: (value: boolean) => void;
  startStoryRun: DevicePreviewController['startStoryRun'];
  setSceneContent: () => void;
}

const DevicePreviewContext = createContext<DevicePreviewContextType | null>(null);

export const useDevicePreview = (): DevicePreviewContextType => {
  const context = useContext(DevicePreviewContext);
  if (!context) throw new Error('useDevicePreview must be used within a DevicePreviewProvider');
  return context;
};

/** The context, or null outside a provider. */
export const useDevicePreviewOptional = () => useContext(DevicePreviewContext);

export const DevicePreviewProvider: React.FC<{ children: React.ReactNode; controller?: DevicePreviewController }> = ({
  children,
  controller: given,
}) => {
  const [controller] = useState(() => given ?? new DevicePreviewController());
  const state = useSyncExternalStore(controller.subscribe, controller.getState);

  useEffect(() => {
    controller.attach();
    return () => controller.detach();
  }, [controller]);

  const value: DevicePreviewContextType = {
    ...state,
    isConnected: state.status === 'connected',
    controller,
    createSession: controller.createSession,
    connect: controller.connect,
    disconnect: controller.disconnect,
    sendSceneData: controller.sendSceneData,
    startDevicePreview: controller.startDevicePreview,
    stopDevicePreview: controller.stopDevicePreview,
    seekDevice: controller.seekDevice,
    setPreviewOnDevice: controller.setPreviewOnDevice,
    startStoryRun: controller.startStoryRun,
    setSceneContent: controller.setSceneContent,
  };

  return <DevicePreviewContext.Provider value={value}>{children}</DevicePreviewContext.Provider>;
};

// ── Scene sync ─────────────────────────────────────────────────────────────

export const SCENE_SYNC_DEBOUNCE_MS = 300;
export const SEEK_INTERVAL_MS = 100;

/**
 * Keep connected devices on the scene being edited (the scene editor mounts
 * it once): `scene-data` when a player joins, when Preview starts and 300 ms
 * after content edits; one `preview-start` / `preview-stop` per Preview with
 * "Remote" selected; `seek` (at most every 100 ms) while scrubbing paused.
 */
export function useDeviceSceneSync(): void {
  const device = useDevicePreviewOptional();
  const controller = device?.controller ?? null;

  useEffect(() => {
    if (!controller) return;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    let seekTimer: ReturnType<typeof setTimeout> | null = null;
    let lastSeekAt = -Infinity;
    let pendingSeek: number | null = null;
    let remotePlaying = false;

    const live = () => controller.getState().status === 'connected';
    const sendScene = () => {
      if (debounce) clearTimeout(debounce);
      debounce = null;
      const scene = useSceneStore.getState().getPlayableScene();
      if (scene) controller.sendSceneData(scene);
    };
    const scheduleScene = () => {
      if (debounce) clearTimeout(debounce);
      // A story run started meanwhile: the scene stays off the device.
      debounce = setTimeout(() => (story() ? undefined : sendScene()), SCENE_SYNC_DEBOUNCE_MS);
    };
    const flushSeek = () => {
      seekTimer = null;
      if (pendingSeek === null) return;
      lastSeekAt = Date.now();
      controller.seekDevice(pendingSeek);
      pendingSeek = null;
    };
    const seek = (playhead: number) => {
      pendingSeek = playhead;
      const wait = lastSeekAt + SEEK_INTERVAL_MS - Date.now();
      if (wait <= 0 && !seekTimer) flushSeek();
      else if (!seekTimer) seekTimer = setTimeout(flushSeek, Math.max(0, wait));
    };

    // While the device plays a story (spec 013 v7) the scene is not sent on a join or an edit, and no seek.
    const story = () => controller.getState().content.kind === 'story';
    const offJoin = controller.onPlayerJoined(() => {
      if (!story()) sendScene();
    });
    if (live() && !story() && useSceneStore.getState().scene) scheduleScene();

    const offStore = useSceneStore.subscribe((state, prev) => {
      if (state.scene !== prev.scene || state.keyframes !== prev.keyframes) {
        if (state.scene && live() && !story()) scheduleScene();
      }
      if (state.isPlaying && !prev.isPlaying) {
        if (live() && controller.getState().previewOnDevice) {
          // Preview on the device is the way back from a story to the scene.
          controller.setSceneContent();
          sendScene();
          controller.startDevicePreview(state.playheadPosition);
          remotePlaying = true;
        }
      } else if (!state.isPlaying && prev.isPlaying) {
        if (remotePlaying) controller.stopDevicePreview();
        remotePlaying = false;
      } else if (!state.isPlaying && state.playheadPosition !== prev.playheadPosition && live() && !story()) {
        seek(state.playheadPosition);
      }
    });

    // A spritesheet cut loaded after the scene (spec 033) reaches the device too.
    const offSheets = useMediaSheetStore.subscribe((state, prev) => {
      if (state.sheets !== prev.sheets && useSceneStore.getState().scene && live() && !story()) scheduleScene();
    });

    return () => {
      offJoin();
      offStore();
      offSheets();
      if (debounce) clearTimeout(debounce);
      if (seekTimer) clearTimeout(seekTimer);
      if (remotePlaying) controller.stopDevicePreview();
    };
  }, [controller]);
}

export default DevicePreviewContext;

// ── Story sync (spec 013 v7) ─────────────────────────────────────────────────

/**
 * Keep connected devices on a story run (the story editor mounts it once):
 * while the controller's content is a story, `story-data` is sent at once
 * for a new run, when a player joins (or a reconnect finds players), and
 * 300 ms after a change to the story's graph or scenes, or to `sceneStore`'s
 * scene or keyframes for a scene in the story (same `runId`). The manifest is
 * the editor runner's (`buildPreviewManifest` of `loadStoryPreviewScenes`),
 * with every unsaved edit; nothing is saved. Unmounting returns the content
 * to the scene.
 */
export function useDeviceStorySync(story: StoryApiResponse | null | undefined): void {
  const device = useDevicePreviewOptional();
  const controller = device?.controller ?? null;
  const storyRef = React.useRef(story);
  storyRef.current = story;

  useEffect(() => {
    if (!controller) return;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    let seq = 0;
    let lastRunId = 0;
    let disposed = false;

    const current = (): StoryApiResponse | null => useStoryStore.getState().story ?? storyRef.current ?? null;
    const run = () => {
      const content = controller.getState().content;
      return content.kind === 'story' ? content : null;
    };
    const live = () => controller.getState().status === 'connected';

    const sendStory = () => {
      if (debounce) clearTimeout(debounce);
      debounce = null;
      const content = run();
      const s = current();
      if (!content || !s || Number(s.id) !== content.storyId) return;
      const mine = ++seq;
      void loadStoryPreviewScenes(s).then((scenes) => {
        // A newer send, another run, or the editor gone: this one is stale.
        const now = run();
        if (disposed || mine !== seq || !now || now.runId !== content.runId) return;
        controller.sendStoryData({
          storyId: content.storyId,
          runId: content.runId,
          ...(content.startAtStorySceneId !== undefined ? { startAtStorySceneId: content.startAtStorySceneId } : {}),
          manifest: buildPreviewManifest(s, scenes),
        });
      });
    };
    const scheduleStory = () => {
      if (!run() || !live()) return;
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(sendStory, SCENE_SYNC_DEBOUNCE_MS);
    };

    const offJoin = controller.onPlayerJoined(() => {
      if (run()) sendStory();
    });
    const offController = controller.subscribe(() => {
      const content = run();
      if (!content || content.runId === lastRunId) return;
      lastRunId = content.runId;
      if (live()) sendStory();
    });
    const offStory = useStoryStore.subscribe((state, prev) => {
      const a = state.story;
      const b = prev.story;
      if (!a || !b) return;
      if (a.graph !== b.graph || a.storyScenes !== b.storyScenes) scheduleStory();
    });
    const offScene = useSceneStore.subscribe((state, prev) => {
      if (state.scene === prev.scene && state.keyframes === prev.keyframes) return;
      // Opening or switching to another scene is not an edit of the story.
      if (!prev.scene || String(prev.scene.id) !== String(state.scene?.id)) return;
      const sceneId = Number(state.scene?.id);
      const s = current();
      if (!s || !Number.isFinite(sceneId) || !s.storyScenes.some((ss) => ss.sceneId === sceneId)) return;
      scheduleStory();
    });

    return () => {
      disposed = true;
      offJoin();
      offController();
      offStory();
      offScene();
      if (debounce) clearTimeout(debounce);
      // Leaving the story: the device plays the scene again (nothing is sent).
      controller.setSceneContent();
    };
  }, [controller]);
}
